<template>
  <div class="dflexc-jcs blog-admin-panel">
    <h1 class="data-entry">Blog Posts</h1>
    <div class="dflexc-jcs data-entry-form">
      <div class="dflexr-jcsa form-row">
        <div class="input-group">
          <label>Topic</label>
          <select v-model.number="form.topic_id">
            <option :value="0" disabled>Select topic</option>
            <option v-for="t in topics" :key="t.id" :value="t.id">{{ t.title }}</option>
          </select>
        </div>
        <div class="input-group" style="flex: 0 0 7rem;">
          <label>Published</label>
          <input type="checkbox" v-model="form.published" />
        </div>
      </div>
      <div class="dflexr-jcsa form-row">
        <div class="input-group">
          <label>Title</label>
          <input v-model="form.title" />
        </div>
        <div class="input-group">
          <label>Slug</label>
          <input v-model="form.slug" />
        </div>
      </div>
      <div class="input-group">
        <div class="dflexr-jcsb body-label-row">
          <label for="post-body">Body</label>
          <button type="button" class="insert-image" :disabled="uploading" @click="pickImage">
            {{ uploading ? "Uploading…" : "Insert image" }}
          </button>
        </div>
        <textarea id="post-body" ref="bodyField" v-model="form.body" rows="8" @paste="onBodyPaste" />
        <input
          ref="imageInput"
          class="behidden"
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          @change="onImagePicked"
        />
        <p v-if="imageError" class="image-error">{{ imageError }}</p>
        <p class="image-hint">Paste an image to embed it at the cursor. Insert image uploads a file. http and https addresses become links.</p>
      </div>
      <div class="dflexr-jcsb data-entry-action-row">
        <div class="dflexr-jcs">
          <BtnCommon btnLabel="Add" styleclass="action-btn-add"
                     :isDisabled="!canAdd" @clicked="addPost" />
          <BtnCommon btnLabel="Save" styleclass="action-btn-save"
                     :isDisabled="!editing" @clicked="savePost" />
          <BtnCommon btnLabel="Cancel" styleclass="action-btn-cancel"
                     :isDisabled="!editing && !form.title" @clicked="resetForm" />
        </div>
        <BtnCommon btnLabel="Remove" styleclass="action-btn-remove"
                   :isDisabled="!editing" @clicked="removePost" />
      </div>
    </div>
    <div class="dflexr-jcs filter-row">
      <label for="post-topic-filter">Show</label>
      <select id="post-topic-filter" v-model="topicFilter">
        <option value="all">All</option>
        <option v-for="t in topics" :key="t.id" :value="String(t.id)">{{ t.title }}</option>
      </select>
    </div>
    <div class="list-view">
      <div class="dflexr-jcs list-header">
        <div class="col topic">Topic</div>
        <div class="col title">Title</div>
        <div class="col slug">Slug</div>
        <div class="col pub">Pub</div>
        <div class="col act">Actions</div>
      </div>
      <div class="dflexr-jcs list-row" v-for="(p, idx) in visiblePosts" :key="p.id"
           :class="{ striped: idx % 2 === 0 }">
        <div class="col topic">{{ topicTitle(p.topic_id) }}</div>
        <div class="col title">{{ p.title }}</div>
        <div class="col slug">{{ p.slug }}</div>
        <div class="col pub">{{ p.published ? 'Y' : 'N' }}</div>
        <div class="col act" @click="editPost(p)">Edit</div>
      </div>
      <p v-if="!visiblePosts.length" class="empty-posts">
        {{ topicFilter === "all" ? "No posts." : "No posts for this topic." }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeMount, reactive, ref } from "vue";
import BtnCommon from "@/components/common/btn-common.vue";
import { BlogService } from "@/services/blog_service";
import { inlineImageUrl } from "@/lib/post_body";

const blog = new BlogService();
const topics = ref([]);
const posts = ref([]);
const topicFilter = ref("all");
const editing = ref(false);
const bodyField = ref(null);
const imageInput = ref(null);
const uploading = ref(false);
const imageError = ref("");
const form = reactive({
  id: null,
  topic_id: 0,
  title: "",
  slug: "",
  body: "",
  published: false
});

const canAdd = computed(
  () => !editing.value && form.topic_id && form.title && form.slug
);

const visiblePosts = computed(() => {
  if (topicFilter.value === "all") return posts.value;
  const topicId = Number(topicFilter.value);
  return posts.value.filter((p) => p.topic_id === topicId);
});

function topicTitle(topicId) {
  const topic = topics.value.find((t) => t.id === topicId);
  return topic ? topic.title : "";
}

function resetForm() {
  form.id = null;
  form.topic_id = topics.value[0]?.id || 0;
  form.title = "";
  form.slug = "";
  form.body = "";
  form.published = false;
  editing.value = false;
  imageError.value = "";
}

function pickImage() {
  imageError.value = "";
  imageInput.value?.click();
}

function altFrom(name) {
  return name.replace(/\.[^.]+$/, "").replace(/[\]\r\n]/g, "").trim().slice(0, 80);
}

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const PASTE_IMAGE_TYPE = /^image\/(png|jpe?g|gif|webp)$/;

function clipboardImage(event) {
  const items = event.clipboardData?.items;
  if (items) {
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) return item.getAsFile();
    }
  }
  const files = event.clipboardData?.files;
  if (files) {
    for (const file of files) {
      if (file.type.startsWith("image/")) return file;
    }
  }
  return null;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function onBodyPaste(event) {
  const file = clipboardImage(event);
  if (!file) return;
  event.preventDefault();
  if (file.type && !PASTE_IMAGE_TYPE.test(file.type)) {
    imageError.value = "Paste a JPEG, PNG, GIF, or WebP image.";
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    imageError.value = "Image must be 8 MB or smaller.";
    return;
  }
  imageError.value = "";
  try {
    const url = inlineImageUrl(await readAsDataUrl(file));
    if (!url) {
      imageError.value = "Could not read that image.";
      return;
    }
    insertImage(url, "image");
  } catch (e) {
    imageError.value = "Could not read that image.";
    console.error(e);
  }
}

function insertImage(url, alt) {
  const el = bodyField.value;
  const value = form.body || "";
  const start = el ? el.selectionStart : value.length;
  const end = el ? el.selectionEnd : value.length;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const lead = before.length && !before.endsWith("\n") ? "\n" : "";
  const trail = after.length && !after.startsWith("\n") ? "\n" : "";
  const snippet = `${lead}![${alt}](${url})${trail}`;
  form.body = before + snippet + after;
  const cursor = before.length + snippet.length;
  nextTick(() => {
    if (!el) return;
    el.focus();
    el.selectionStart = el.selectionEnd = cursor;
  });
}

async function onImagePicked(event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  if (file.size > MAX_IMAGE_BYTES) {
    imageError.value = "Image must be 8 MB or smaller.";
    return;
  }
  uploading.value = true;
  imageError.value = "";
  try {
    const pkg = await blog.adminUploadImage(file);
    if (!pkg.url) {
      const errors = pkg.errors;
      imageError.value = Array.isArray(errors) ? errors.join(", ") : (errors || "Image upload failed.");
      return;
    }
    insertImage(pkg.url, altFrom(file.name));
  } catch (e) {
    imageError.value = "Image upload failed.";
    console.error(e);
  } finally {
    uploading.value = false;
  }
}

async function load() {
  const [tPkg, pPkg] = await Promise.all([
    blog.adminListTopics(),
    blog.adminListPosts()
  ]);
  topics.value = tPkg.topics || [];
  posts.value = pPkg.posts || [];
  if (!form.topic_id && topics.value.length) {
    form.topic_id = topics.value[0].id;
  }
}

function editPost(p) {
  Object.assign(form, {
    id: p.id,
    topic_id: p.topic_id,
    title: p.title,
    slug: p.slug,
    body: p.body || "",
    published: !!p.published
  });
  editing.value = true;
}

async function addPost() {
  await blog.adminSavePost({ ...form, id: null });
  resetForm();
  await load();
}

async function savePost() {
  await blog.adminSavePost({ ...form });
  resetForm();
  await load();
}

async function removePost() {
  if (!form.id) return;
  if (!confirm("Delete post " + form.title + "?")) return;
  await blog.adminDeletePost(form.id);
  resetForm();
  await load();
}

onBeforeMount(load);
</script>

<style scoped lang="scss">
@import "@/assets/styles/admin/user-table-input-form";
@import "@/assets/styles/blog-admin";
</style>
